<script setup lang="ts">
/**
 * La card di un annuncio «Novità»: titolo, testo breve, pulsante e «×». È solo presentazione: lo usano il banner in alto
 * nel layout e l'anteprima della pagina di amministrazione. Testi semplici (interpolazione, mai v-html).
 */
import { computed, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Novita } from '../stores/novita'
import { percorsoInterno, useTestiNovita } from '../lib/testiNovita'

const props = defineProps<{
  annuncio: Novita
  /** Mostra la «×» (nell'anteprima si può togliere). */
  chiudibile?: boolean
}>()
const emit = defineEmits<{ (e: 'apri'): void; (e: 'chiudi'): void }>()

const { t } = useI18n()
const { testo } = useTestiNovita()
const idTitolo = `novita-${useId()}`

const titolo = computed(() => testo(props.annuncio.chiave, 'titolo', props.annuncio.titolo))
const breve = computed(() => testo(props.annuncio.chiave, 'breve', props.annuncio.testoBreve))
const pulsante = computed(() => testo(props.annuncio.chiave, 'pulsante', props.annuncio.etichettaPulsante))
const haPulsante = computed(() => !!percorsoInterno(props.annuncio.link) && !!pulsante.value)
const immagine = computed(() => percorsoInterno(props.annuncio.immagineUrl))
</script>

<template>
  <section
    class="novita-card"
    role="region"
    :aria-labelledby="idTitolo"
    data-testid="novita-card"
    @keydown.esc="chiudibile && emit('chiudi')"
  >
    <img
      v-if="immagine"
      class="novita-card__img"
      :src="immagine"
      alt=""
    >
    <span
      v-else
      class="novita-card__icona"
      aria-hidden="true"
    ><i class="pi pi-megaphone" /></span>
    <div class="novita-card__corpo">
      <p class="novita-card__etichetta">
        {{ t('novita.etichetta', 'Novità') }}
      </p>
      <h2
        :id="idTitolo"
        class="novita-card__titolo"
      >
        {{ titolo }}
      </h2>
      <p class="novita-card__testo">
        {{ breve }}
      </p>
    </div>
    <div class="novita-card__azioni">
      <Button
        v-if="haPulsante"
        :label="pulsante"
        icon="pi pi-arrow-right"
        icon-pos="right"
        size="small"
        data-testid="novita-apri"
        @click="emit('apri')"
      />
      <Button
        v-if="chiudibile"
        icon="pi pi-times"
        severity="secondary"
        text
        rounded
        size="small"
        data-testid="novita-chiudi"
        :aria-label="t('novita.chiudi', 'Chiudi la novità')"
        @click="emit('chiudi')"
      />
    </div>
  </section>
</template>

<style scoped>
.novita-card {
  display: flex; align-items: center; gap: 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid var(--p-content-border-color);
  border-left: 4px solid var(--p-primary-color);
  border-radius: var(--p-content-border-radius, 8px);
  background: color-mix(in srgb, var(--p-primary-color) 7%, var(--p-content-background));
  color: var(--p-text-color);
}
.novita-card__icona {
  display: inline-flex; align-items: center; justify-content: center; flex: none;
  width: 2.5rem; height: 2.5rem; border-radius: 999px;
  background: color-mix(in srgb, var(--p-primary-color) 16%, transparent);
  color: var(--p-primary-color); font-size: 1.15rem;
}
.novita-card__img { flex: none; width: 3rem; height: 3rem; object-fit: contain; border-radius: 6px; }
.novita-card__corpo { flex: 1 1 auto; min-width: 0; }
.novita-card__etichetta {
  margin: 0; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
  color: var(--p-primary-color);
}
.novita-card__titolo { margin: 0.1rem 0 0.15rem; font-size: 1rem; font-weight: 600; line-height: 1.3; }
.novita-card__testo { margin: 0; font-size: 0.9rem; color: var(--p-text-muted-color); line-height: 1.4; }
.novita-card__azioni { display: flex; align-items: center; gap: 0.25rem; flex: none; }

@media (max-width: 640px) {
  .novita-card { flex-wrap: wrap; align-items: flex-start; gap: 0.6rem 0.75rem; padding: 0.75rem; }
  .novita-card__corpo { flex-basis: calc(100% - 7rem); }
  .novita-card__azioni { flex-basis: 100%; justify-content: space-between; flex-direction: row-reverse; }
  .novita-card__icona { width: 2.1rem; height: 2.1rem; }
}
</style>
